/**
 * Extracts the HTTP status code from an error (typically an Axios error).
 * Returns undefined if the error is not an HTTP error.
 */
export function getHttpStatus(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status
}

/**
 * Whether an HTTP error indicates the resource doesn't exist or the user
 * lacks permission to access it. Used to show a unified "not available"
 * error page instead of leaking technical details.
 */
export function isNotFoundOrForbidden(error: unknown): boolean {
  const status = getHttpStatus(error)
  return status === 400 || status === 403 || status === 404
}

/**
 * The message the API actually sent, not Axios's own.
 *
 * An Axios rejection stringifies to "Request failed with status code 409",
 * which tells a user nothing — the reason ("Field \"x\" already exists") is in
 * the response body. Every API error is enveloped as
 * `{ error: { code, message, details, correlation_id } }` by the backend's
 * error-handler middleware, so read that first and fall back outward.
 */
export function apiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  const data = (error as {
    response?: { data?: { error?: { message?: string }; message?: string } }
  })?.response?.data
  const fromEnvelope = data?.error?.message || data?.message
  if (fromEnvelope) return fromEnvelope
  // Axios's own message is a last resort — only useful when there is no body
  // at all (network failure, CORS, timeout).
  if (error instanceof Error && !(error as { response?: unknown }).response) return error.message
  return fallback
}
